import { useEffect } from "react"
import { X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { sanitizeRichText, isHtmlContent } from "@/lib/sanitize"

interface Props {
    open: boolean
    onClose: () => void
    title: string
    content: string
}

export const OperatingRulesModal = ({ open, onClose, title, content }: Props) => {
    const { t } = useTranslation()

    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
        document.addEventListener("keydown", onKey)
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = "hidden"
        return () => {
            document.removeEventListener("keydown", onKey)
            document.body.style.overflow = prevOverflow
        }
    }, [open, onClose])

    if (!open) return null

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div className="absolute inset-0 bg-black/50" />
            <div
                className="relative bg-white rounded-3xl sm:rounded-[40px] shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <div className="flex items-center justify-between gap-2 pl-5 sm:pl-8 pr-2 sm:pr-4 pt-5 sm:pt-8 pb-4 border-b border-slate-100">
                    <h2 className="text-xl font-black text-slate-900">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("guest.campDetail.closeRules")}
                        className="shrink-0 size-11 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                        <X className="size-5" />
                    </button>
                </div>

                <div className="overflow-y-auto px-6 sm:px-8 py-6">
                    {isHtmlContent(content)
                        ? <div className="text-slate-700 font-medium leading-relaxed rich-text" dangerouslySetInnerHTML={{ __html: sanitizeRichText(content) }} />
                        : <p className="text-slate-700 font-medium whitespace-pre-line leading-relaxed">{content}</p>
                    }
                </div>

                <div className="px-6 sm:px-8 py-4 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full bg-slate-100 text-slate-700 font-bold rounded-[28px] py-3 px-6 hover:bg-slate-200 transition-all active:scale-95"
                    >
                        {t("guest.campDetail.closeRules")}
                    </button>
                </div>
            </div>
        </div>
    )
}
